<script setup>
import { ref, computed, onMounted, defineProps, defineEmits } from 'vue';
import Data from '@/api/data';
import InfoIcon from '@/components/common/InfoIcon.vue';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/20/solid';
import SetupCalculation from '@/components/modals/SetupCalculation.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  dataID: {
    type: String,
    required: true
  },
  widgetTitle: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:modelValue']);

// Local state
const config = ref({ ...props.modelValue });
const loadingData = ref(false);
const subcollections = ref({});
const validFields = ref({});
const expandedAdvanced = ref(new Set());
const showCalcModal = ref(false);

const toggleAdvanced = (idx) => {
  if (expandedAdvanced.value.has(idx)) {
    expandedAdvanced.value.delete(idx);
  } else {
    expandedAdvanced.value.add(idx);
  }
  // Trigger reactivity
  expandedAdvanced.value = new Set(expandedAdvanced.value);
};

// Lifecycle
onMounted(async () => {
  await getSubcollections();
  // Only add a default plot line when creating a new chart (no existing specifications)
  const hasExistingLines = Object.values(config.value.specifications || {}).some(lines => lines.length > 0);
  if (!hasExistingLines) {
    addPlotLine();
  }
});

// API Methods
const getSubcollections = async () => {
  loadingData.value = true;
  // Show a placeholder plot line so the full "Plot Line settings" card is visible while loading
  const hasExistingLines = Object.values(config.value.specifications || {}).some(lines => lines.length > 0);
  if (!hasExistingLines) {
    if (!config.value.specifications) config.value.specifications = {};
    config.value.specifications['__loading__'] = [{
      field: '',
      friendlyName: '',
      color: defaultLineColors[0],
      unit: '',
      dataFrequencySeconds: 300
    }];
    emit('update:modelValue', config.value);
  }
  try {
    const response = await Data.get(props.dataID);
    if (response.body.status === 'success') {
      const ignoreSubcollections = ['Net_Cfg', 'action_request', 'action_result', 'location', 'messages'];
      subcollections.value = Object.fromEntries(
        Object.entries(response.body.details)
          .filter(([key]) => !ignoreSubcollections.includes(key))
          .sort(([a], [b]) => a.localeCompare(b))
      );

      // Build valid fields for each subcollection
      Object.entries(subcollections.value).forEach(([source, fields]) => {
        validFields.value[source] = Object.entries(fields)
          .filter(([, type]) => type === 'int' || type === 'float' || type === 'bool')
          .map(([field]) => field)
          .sort((a, b) => a.localeCompare(b));
      });
    }
  } catch (err) {
    console.error('Error fetching subcollections:', err);
  } finally {
    loadingData.value = false;
    // Replace placeholder with real first plot line if we had no existing lines
    if (config.value.specifications && config.value.specifications['__loading__']) {
      delete config.value.specifications['__loading__'];
      emit('update:modelValue', config.value);
      const hasOtherLines = Object.values(config.value.specifications || {}).some(lines => lines.length > 0);
      if (!hasOtherLines) {
        addPlotLine();
      }
    }
  }
};

const emitUpdate = () => {
  const toEmit = { ...config.value };
  if (toEmit.specifications && toEmit.specifications['__loading__']) {
    delete toEmit.specifications['__loading__'];
  }
  emit('update:modelValue', toEmit);
};

// Methods for managing plot lines
const defaultLineColors = [
  '#3b82f6', // Blue
  '#10b981', // Green
  '#f59e0b', // Orange
  '#ef4444', // Red
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#14b8a6', // Teal
  '#f97316', // Dark Orange
];

const addPlotLine = () => {
  if (!config.value.specifications) {
    config.value.specifications = {};
  }
  
  // Get first available source
  const firstSource = Object.keys(subcollections.value)[0];
  if (!firstSource) return;
  
  if (!config.value.specifications[firstSource]) {
    config.value.specifications[firstSource] = [];
  }
  
  // Count total plot lines for color selection
  let totalLines = 0;
  Object.values(config.value.specifications).forEach(lines => {
    totalLines += lines.length;
  });
  
  const colorIndex = totalLines % defaultLineColors.length;
  
  config.value.specifications[firstSource].push({
    field: '',
    friendlyName: '',
    color: defaultLineColors[colorIndex],
    unit: '',
    dataFrequencySeconds: 300
  });
  
  emitUpdate();
};

const removePlotLine = (source, index) => {
  config.value.specifications[source].splice(index, 1);
  if (config.value.specifications[source].length === 0) {
    delete config.value.specifications[source];
  }
  emitUpdate();
};

const updatePlotLineSource = (oldSource, index, newSource) => {
  if (newSource === '__new_source__') {
    showCalcModal.value = true;
    return;
  }
  const plotLine = config.value.specifications[oldSource][index];
  
  // Remove from old source
  config.value.specifications[oldSource].splice(index, 1);
  if (config.value.specifications[oldSource].length === 0) {
    delete config.value.specifications[oldSource];
  }
  
  // Add to new source
  if (!config.value.specifications[newSource]) {
    config.value.specifications[newSource] = [];
  }
  
  // Reset field when source changes
  plotLine.field = '';
  config.value.specifications[newSource].push(plotLine);
  
  emitUpdate();
};

const handleNewSource = () => {
  showCalcModal.value = false;
  getSubcollections();
};

// Get all plot lines as flat array for easier iteration
const flatPlotLines = computed(() => {
  const lines = [];
  Object.entries(config.value.specifications || {}).forEach(([source, specifications]) => {
    specifications.forEach((spec, index) => {
      lines.push({ source, index, ...spec });
    });
  });
  return lines;
});
</script>

<template>
  <div class="space-y-6">
    <!-- Configuration Section -->
    <div class="space-y-4">
      <!-- Plot Lines -->
      <div v-if="flatPlotLines.length === 0 && !loadingData" class="text-center py-8 text-gray-500 dark:text-gray-400">
        <p>No plot lines configured. Click "Add Plot Line" to get started.</p>
      </div>

      <div v-for="(line, idx) in flatPlotLines" :key="`${line.source}-${line.index}`" class="p-4 border border-gray-300 dark:border-gray-600 rounded-lg space-y-3">
          <div class="flex justify-between items-center">
            <h5 class="font-medium text-gray-700 dark:text-gray-300">Plot Line {{ idx + 1 }}</h5>
            <button
              v-if="line.source !== '__loading__'"
              @click="removePlotLine(line.source, line.index)"
              type="button"
              class="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              Remove
            </button>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Data Source *
                <span v-if="loadingData" class="inline-flex items-center ml-2" title="Loading options...">
                  <span class="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
                </span>
              </label>
              <select
                :value="line.source"
                @change="updatePlotLineSource(line.source, line.index, $event.target.value)"
                :disabled="loadingData"
                class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option v-if="line.source === '__loading__'" value="__loading__">Loading sources...</option>
                <option value="__new_source__">+ New Source</option>
                <option v-for="source in Object.keys(subcollections)" :key="source" :value="source">
                  {{ source }}
                </option>
              </select>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Field Name *
              </label>
              <select
                v-model="config.specifications[line.source][line.index].field"
                @change="emitUpdate"
                :disabled="loadingData || line.source === '__loading__'"
                class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Select a field...</option>
                <option v-for="field in (validFields[line.source] || [])" :key="field" :value="field">
                  {{ field }}
                </option>
              </select>
            </div>
          </div>

          <div class="grid grid-cols-3 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Display Name
              </label>
              <input
                v-model="config.specifications[line.source][line.index].friendlyName"
                type="text"
                placeholder="e.g., Temperature"
                :disabled="line.source === '__loading__'"
                @input="emitUpdate"
                class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Unit
              </label>
              <input
                v-model="config.specifications[line.source][line.index].unit"
                type="text"
                placeholder="e.g., °F"
                :disabled="line.source === '__loading__'"
                @input="emitUpdate"
                class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Line Color
              </label>
              <input
                v-model="config.specifications[line.source][line.index].color"
                type="color"
                :disabled="line.source === '__loading__'"
                @input="emitUpdate"
                class="w-full h-10 px-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <!-- Advanced Options -->
          <div class="border-t border-gray-200 dark:border-gray-600 pt-3">
            <button
              type="button"
              @click="toggleAdvanced(idx)"
              class="w-full flex items-center justify-between text-sm font-semibold text-gray-600 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
            >
              <span>Advanced Options</span>
              <ChevronUpIcon v-if="expandedAdvanced.has(idx)" class="w-4 h-4" />
              <ChevronDownIcon v-else class="w-4 h-4" />
            </button>

            <div v-if="expandedAdvanced.has(idx)" class="mt-3">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Gap Threshold (seconds)
                <InfoIcon
                  content="How often data points are sent from the device.

This prevents the chart from drawing long, misleading lines across periods where no data was recorded, such as during a sensor failure or system downtime."
                  class="ml-1"
                />
              </label>
              <input
                type="number"
                :value="config.specifications[line.source][line.index].dataFrequencySeconds ?? 300"
                @input="config.specifications[line.source][line.index].dataFrequencySeconds = parseInt($event.target.value); emitUpdate()"
                placeholder="300"
                min="1"
                :disabled="line.source === '__loading__'"
                class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>
        </div>

        <div class="flex justify-between items-center">
          <button
            @click="addPlotLine"
            type="button"
            :disabled="loadingData"
            class="text-sm px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Add Plot Line
          </button>
        </div>
    </div>
  </div>

  <SetupCalculation
    v-if="showCalcModal"
    :dataID="dataID"
    :subcollectionOptions="subcollections"
    @calculation-details="handleNewSource"
    @close="showCalcModal = false"
  />
</template>
